import { useEffect, useState } from 'react';

import { isClientSide } from '@confluence/meta';

/**
 * Resolve the first DOM element matching `selector` and re-resolve it whenever
 * the document mutates. Returns `null` while the element is not present in the
 * DOM (and during SSR, where `document` is not available).
 *
 * Pass `disabled: true` to short-circuit both the initial lookup and the
 * `MutationObserver`. This is the recommended way to opt out cheaply when
 * the caller has already determined the lookup is unnecessary (e.g. the
 * onboarding key tells us the spotlight will never show again) — the
 * observer would otherwise run a `document.querySelector` on every DOM
 * mutation in `document.body` for the lifetime of the component.
 *
 * The hook is fully SSR-safe — the effect, `MutationObserver`, and any DOM
 * lookups only run inside `useEffect`, which never executes during server-side
 * rendering or React hydration. The initial state is always `null`, so first
 * client render matches server output and hydration is not affected.
 */
export function useElementBySelector(
	selector: string,
	{ disabled = false }: { disabled?: boolean } = {},
): Element | null {
	const [element, setElement] = useState<Element | null>(null);

	useEffect(() => {
		// Guard against environments without a `document` (SSR, tests).
		// `isClientSide()` is the canonical Confluence helper for this check
		// (`@confluence/meta`).
		if (!isClientSide()) {
			return;
		}

		// Caller has opted out of the lookup entirely — don't run the
		// initial `querySelector`, don't attach a `MutationObserver`.
		if (disabled) {
			return;
		}

		const resolve = () => {
			const next = document.querySelector(selector);
			// Functional update so we don't trigger re-renders when the element
			// reference is unchanged.
			setElement((current) => (current === next ? current : next));
		};

		resolve();

		const observer = new MutationObserver(resolve);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			observer.disconnect();
			setElement(null);
		};
	}, [selector, disabled]);

	return element;
}
